import {
  fnlink,
  div,
  h1,
  button,
  span,
  figure,
  img,
  fnstate,
  input,
  option,
  select
} from '@srfnstack/fntags'
import { createPaginatedList } from '../utils/pagination.js'

const STATUS_OPTIONS = [
  { value: '', label: 'All Active' },
  { value: 'NOT_PUBLISHED', label: 'Not Published' },
  { value: 'READY_FOR_REVIEW', label: 'Ready for Review' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'PROCESSING', label: 'Processing' },
  { value: 'PROCESSING_SEO', label: 'Processing SEO' },
  { value: 'PUSHED_TO_PRINTIFY', label: 'Pushed to Printify' },
  { value: 'QUEUED_FOR_PUBLISH', label: 'Queued for Publish' },
  { value: 'PUBLISHED', label: 'Published' },
  { value: 'FAILED', label: 'Rejected' },
  { value: 'ALL', label: 'All Statuses' }
]

export const ProductReview = () => {
  const statusFilter = fnstate('')
  const productTypeFilter = fnstate('')
  const search = fnstate('')
  let searchTimer = null
  const productTypes = fnstate([], t => t.keyword)
  const selectedProducts = fnstate([])
  const SCROLL_KEY = 'productReview_scrollY'

  const buildUrl = (offset, limit) => {
    const params = new URLSearchParams({ limit, offset, _: Date.now() })
    const s = statusFilter()
    if (s === 'ALL') params.set('include_rejected', 'true')
    else if (s === 'NOT_PUBLISHED') params.set('not_published', 'true')
    else if (s) params.set('status', s)
    const pt = productTypeFilter()
    if (pt) params.set('product_type', pt)
    const q = search().trim()
    if (q) params.set('search', q)
    return `/api/product-concepts?${params}`
  }

  // The request-token guard, the "only the newest request clears the spinner"
  // rule, and the stop-at-total check all live in createPaginatedList now.
  // onReset clears the selection, which was built from rows the new filter may
  // no longer return.
  const list = createPaginatedList({
    buildUrl,
    limit: 20,
    onReset: () => selectedProducts([])
  })
  const { items, total, loading, loadingMore, sentinel } = list
  const fetchProducts = list.reload

  statusFilter.subscribe(fetchProducts)
  productTypeFilter.subscribe(fetchProducts)

  fetch('/api/system/product-types').then(r => r.ok && r.json().then(types => productTypes(types)))

  let scrollRestored = false
  items.subscribe(() => {
    if (scrollRestored) return
    scrollRestored = true
    const saved = sessionStorage.getItem(SCROLL_KEY)
    if (saved) {
      sessionStorage.removeItem(SCROLL_KEY)
      requestAnimationFrame(() => window.scrollTo(0, parseInt(saved, 10)))
    }
  })

  const handleBulkPush = async () => {
    const sel = selectedProducts()
    if (sel.length === 0) return
    if (await window.confirm(`Push ${sel.length} products to Printify? (Includes re-push for already-pushed products)`)) {
      const res = await fetch('/api/product-concepts/bulk-push-to-printify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product_ids: sel })
      })
      if (res.ok) {
        const data = await res.json()
        await window.alert(`Successfully queued ${data.count} products for pushing to Printify!`)
        selectedProducts([])
        fetchProducts()
      } else {
        const err = await res.json()
        await window.alert('Failed: ' + (err.error || 'Unknown error'))
      }
    }
  }

  const handleBulkPublish = async () => {
    const sel = selectedProducts()
    if (sel.length === 0) return
    if (await window.confirm(`Publish ${sel.length} products to Etsy? (Must be pushed to Printify first)`)) {
      const res = await fetch('/api/product-concepts/bulk-publish-to-etsy', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product_ids: sel })
      })
      if (res.ok) {
        const data = await res.json()
        await window.alert(`Successfully queued ${data.count} products for Etsy publishing!`)
        selectedProducts([])
        fetchProducts()
      } else {
        const err = await res.json()
        await window.alert('Failed: ' + (err.error || 'Unknown error'))
      }
    }
  }

  const handleBulkReject = async () => {
    const sel = selectedProducts()
    if (sel.length === 0) return
    if (await window.confirm(`Reject ${sel.length} product concepts?`)) {
      for (const id of sel) {
        await fetch(`/api/product-concepts/${id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: 'FAILED' })
        })
      }
      await window.alert(`Rejected ${sel.length} concepts.`)
      selectedProducts([])
      fetchProducts()
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'READY_FOR_REVIEW': return 'badge-success'
      case 'PUBLISHED': return 'badge-info'
      case 'PUSHED_TO_PRINTIFY': return 'badge-secondary'
      case 'QUEUED_FOR_PUBLISH': return 'badge-accent'
      case 'FAILED': return 'badge-error'
      case 'PENDING':
      case 'PROCESSING':
      case 'PROCESSING_SEO': return 'badge-primary'
      default: return 'badge-ghost'
    }
  }

  const renderCard = (p) => {
    const isProcessing = ['PENDING', 'PROCESSING', 'PROCESSING_SEO'].includes(p.status)
    const displayImage = p.source_asset_path || p.upscaled_image

    return fnlink({
      to: p.status === 'AWAITING_COLOR_SELECTION' ? `/product-init/${p.id}` : `/product-review/${p.id}`,
      onclick: () => sessionStorage.setItem(SCROLL_KEY, window.scrollY),
      class: selectedProducts.bindAttr(sel => `card bg-base-100 shadow-md border ${sel.includes(p.id) ? 'border-primary ring-2 ring-primary/20' : 'border-base-300'} hover:border-primary transition-all cursor-pointer group flex flex-col`)
    },
    figure({ class: 'aspect-square overflow-hidden border-b border-base-300 relative bg-base-200' },
      displayImage
        ? img({
          src: `/api/image?file=${encodeURIComponent(displayImage)}`,
          class: 'object-cover w-full h-full transition-transform group-hover:scale-105'
        })
        : div({ class: 'flex items-center justify-center h-full opacity-20' }, span({ class: 'text-4xl' }, '🖼️')),
      div({ class: 'absolute top-2 left-2' },
        input({
          type: 'checkbox',
          class: 'checkbox checkbox-primary shadow-lg',
          checked: selectedProducts.bindAttr(sel => sel.includes(p.id)),
          onclick: (e) => {
            e.stopPropagation()
            const sel = selectedProducts()
            selectedProducts(sel.includes(p.id) ? sel.filter(id => id !== p.id) : [...sel, p.id])
          }
        })
      ),
      div({ class: 'absolute top-2 right-2' },
        div({ class: `badge ${getStatusColor(p.status)} font-bold shadow-lg border-none` }, p.status)
      ),
      isProcessing
        ? div({ class: 'absolute inset-0 bg-base-300/40 backdrop-blur-[1px] flex items-center justify-center' },
          span({ class: 'loading loading-ring loading-lg text-primary' })
        )
        : ''
    ),
    div({ class: 'card-body p-5 flex flex-col gap-3' },
      div({ class: 'flex justify-between items-start gap-4' },
        div({ class: 'flex flex-col gap-0.5 min-w-0' },
          div({ class: 'text-[10px] uppercase tracking-widest font-black opacity-40' }, p.product_type),
          span({ class: 'text-lg font-bold truncate leading-tight' }, p.design_title || 'Untitled Concept')
        ),
        span({ class: 'text-[10px] font-bold opacity-30 shrink-0 mt-1' },
          new Date(p.created_at).toLocaleDateString()
        )
      ),
      !isProcessing && p.mockups
        ? div({ class: 'flex gap-1.5 overflow-hidden h-10' },
          p.mockups.slice(0, 4).map(m => img({
            src: `/api/image?file=${encodeURIComponent(m)}`,
            class: 'w-10 h-10 object-cover rounded border border-base-300'
          }))
        )
        : div({ class: 'h-10 flex items-center' },
          span({ class: 'text-[10px] uppercase font-bold opacity-30 italic' }, isProcessing ? 'Pipeline processing...' : 'No mockups generated')
        ),
      div({ class: 'card-actions justify-end mt-2' },
        button({ class: 'btn btn-primary btn-sm btn-ghost group-hover:btn-active' }, 'View Details →')
      )
    )
    )
  }

  fetchProducts()

  return div({ class: 'p-4 md:p-6 flex flex-col min-h-[calc(100vh-64px)]' },
    div({ class: 'flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6' },
      div({ class: 'flex items-center gap-3' },
        h1({ class: 'text-2xl md:text-3xl font-bold' }, 'Product Concepts'),
        total.bindAs(t => t > 0 ? span({ class: 'badge badge-ghost font-mono' }, t) : span())
      ),
      div({ class: 'flex items-center gap-3 w-full md:w-auto' },
        input({
          type: 'search',
          class: 'input input-bordered input-sm w-full md:w-64',
          placeholder: 'Search title, design, type…',
          value: search(),
          oninput: (e) => {
            search(e.target.value)
            clearTimeout(searchTimer)
            searchTimer = setTimeout(fetchProducts, 300)
          }
        }),
        select({
          class: 'select select-bordered select-sm',
          onchange: (e) => statusFilter(e.target.value)
        },
        STATUS_OPTIONS.map(o => option({ value: o.value, selected: statusFilter() === o.value }, o.label))
        ),
        productTypes.bindChildren(select({
          class: 'select select-bordered select-sm',
          onchange: (e) => productTypeFilter(e.target.value)
        },
        option({ value: '' }, 'All Types')
        ), (tState) => {
          const t = tState()
          return option({ value: t.keyword }, t.keyword)
        }),
        button({ class: 'btn btn-outline btn-sm', onclick: fetchProducts }, 'Refresh')
      )
    ),

    selectedProducts.bindAs(sel => {
      if (sel.length === 0) return div()
      return div({ class: 'fixed bottom-12 left-1/2 -translate-x-1/2 bg-base-300 p-4 rounded-2xl shadow-2xl flex gap-4 items-center z-[150] border border-primary/30 backdrop-blur-md' },
        span({ class: 'font-bold text-sm px-2' }, `${sel.length} selected`),
        button({ class: 'btn btn-primary btn-sm', onclick: handleBulkPush }, 'Push to Printify'),
        button({ class: 'btn btn-success btn-sm', onclick: handleBulkPublish }, 'Publish to Etsy'),
        button({ class: 'btn btn-error btn-sm', onclick: handleBulkReject }, 'Reject All'),
        button({ class: 'btn btn-ghost btn-sm btn-circle', onclick: () => selectedProducts([]), title: 'Clear' }, '✕')
      )
    }),

    loading.bindAs(l => l
      ? div({ class: 'flex items-center justify-center h-64' }, span({ class: 'loading loading-spinner loading-lg text-primary' }))
      : items.bindAs(list => {
        if (list.length === 0) {
          return div({ class: 'bg-base-100 rounded-xl border border-base-300 p-12 text-center opacity-60 flex flex-col items-center gap-2 shadow-sm' },
            span({ class: 'text-4xl' }, '🛍️'),
            span('No product concepts found.')
          )
        }
        return div({ class: 'flex flex-col gap-6' },
          div({ class: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6' },
            list.map(renderCard)
          ),
          loadingMore.bindAs(lm => lm
            ? div({ class: 'flex justify-center py-6' }, span({ class: 'loading loading-spinner loading-md text-primary' }))
            : div()
          ),
          sentinel
        )
      })
    )
  )
}
